import ErrorIcon from '@mui/icons-material/Error';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import ErrorBox from '../../core/components/ErrorBox';
import useAirbnbSyncErrors from '../hooks/useAirbnbSyncErrors';

const AirbnbSyncErrorWidget = () => {
    const navigate = useNavigate();
    const { data, loading, error } = useAirbnbSyncErrors({
        params: [],
    });

    if (loading) {
        return null;
    }

    if (error) {
        return <ErrorBox error={error} />;
    }

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2.5,
                backgroundColor: '#1a1a2e',
                border: '3px solid #ef4444',
                borderRadius: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ color: '#ef4444' }} />
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#ef4444',
                    }}
                >
                    AirBnB Sync Errors ({data.length})
                </Typography>
            </Box>
            {data.map(syncError => (
                <Box
                    key={syncError.propertyId}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        p: 2,
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid #ef4444',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            color: 'rgba(255, 255, 255, 0.9)',
                        }}
                    >
                        {syncError.propertyName}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {syncError.syncError}
                    </Typography>
                    {syncError.lastSyncAt && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontSize: '0.7rem',
                            }}
                        >
                            {formatDistanceToNow(new Date(syncError.lastSyncAt), {
                                addSuffix: true,
                            })}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => {
                            navigate({
                                to: `/${syncError.propertyId}/settings/modules`,
                            });
                        }}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #ef4444',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            px: 2,
                            py: 0.5,
                            alignSelf: 'flex-start',
                        }}
                    >
                        Fix Configuration
                    </Button>
                </Box>
            ))}
        </Box>
    );
};

export default AirbnbSyncErrorWidget;
